import { TerminationReasons } from "../enums/termination-reasons";
import { BaseRef } from "../models/BaseRef";
import { Policy } from "../models/policy";
import { RequestReason, RequestType } from "../models/request-types.model";
import { TerminationDateConstraints } from "../models/termination-date-constraints";

export class ContactUsHelper {
  static getTerminationDateConstraints(reason: string, policy: Policy | undefined): TerminationDateConstraints {
    const today = new Date();
    if (!policy) {
      return {
        minDate: null,
        maxDate: null,
        defaultDate: null,
        disabled: false,
      };
    }

    switch (reason) {
      case TerminationReasons.ECHEANCE: {
        return {
          minDate: null,
          maxDate: null,
          defaultDate: policy.effectiveEndDate ? new Date(policy.effectiveEndDate) : null,
          disabled: true,
        };
      }

      case TerminationReasons.RENONC: {
        const signatureDate = new Date(policy.policySignatureDate);
        const signatureDatePlus14 = new Date(signatureDate);
        signatureDatePlus14.setDate(signatureDatePlus14.getDate() + 14);
        return {
          minDate: signatureDate,
          maxDate: signatureDatePlus14,
          defaultDate: today,
          disabled: false,
        };
      }

      case TerminationReasons.AUTRE: {
        const futureDate = new Date(today);
        futureDate.setDate(today.getDate() + 30);
        return {
          minDate: today,
          maxDate: futureDate,
          defaultDate: futureDate,
          disabled: false,
        };
      }

      case TerminationReasons.DECES:
      case TerminationReasons.VENTEDEM: {
        const preselectedDate = new Date(today);
        preselectedDate.setDate(today.getDate() + 30);
        return {
          minDate: null,
          maxDate: null,
          defaultDate: preselectedDate,
          disabled: false,
        };
      }

      default: {
        return {
          minDate: null,
          maxDate: null,
          defaultDate: null,
          disabled: false,
        };
      }
    }
  }

  static handleRenoncReason(currentPolicy: Policy | undefined, reasons: RequestReason[]): RequestReason[] {
    if (!currentPolicy?.policySignatureDate) return reasons;

    const signatureDate = new Date(currentPolicy.policySignatureDate);
    const signatureDatePlus14 = new Date(signatureDate);
    signatureDatePlus14.setDate(signatureDatePlus14.getDate() + 14);
    const today = new Date();
    const isValid = today >= signatureDate && today <= signatureDatePlus14;
    return !isValid ? reasons.filter(reason => reason.code !== TerminationReasons.RENONC) : reasons;
  }

  static sortByCreationDate<T extends { creationDate: Date }>(items: T[]): T[] {
    return items.sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime());
  }

  static updateAttestationOptions(
    requestNatureOptions: RequestType[],
    documentTypes: BaseRef[],
    attestationCode: string
  ): BaseRef[] {
    const attestationRequest = requestNatureOptions.find(ele => ele.code === attestationCode);

    if (attestationRequest && attestationRequest.reasons) {
      // Flatten all requiredDocumentsCodes arrays from all reasons into one array.
      const requiredCodes: string[] = attestationRequest.reasons.flatMap(reason => reason.requiredDocumentsCodes);

      // Remove duplicates.
      const uniqueRequiredCodes = Array.from(new Set(requiredCodes));

      // Filter document types that match any of the required codes.
      return documentTypes.filter(docType => uniqueRequiredCodes.includes(docType.code));
    }

    return [];
  }
}
